import { useEffect, useMemo, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { INR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { exportToExcel, exportToPDF } from "@/lib/exports";
import { FileSpreadsheet, FileText, Search, Pencil, X, RefreshCw, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "stock", label: "Stock Report" },
  { key: "transfers", label: "Transfer Report" },
  { key: "finance", label: "Finance Report" },
  { key: "delivery", label: "Date of Delivery" },
  { key: "disbursement", label: "Disbursement", financeOrAdmin: true },
  { key: "model", label: "Model Wise" },
  { key: "branch", label: "Branch Wise" },
  { key: "profit", label: "Net Profit", adminOnly: true },
  { key: "fy-summary", label: "FY Summary", adminOnly: true },
];

const SR_COL = { key: "_sr", label: "SR. No.", thClass: "col-sr", tdClass: "col-sr" };

// One shared fetch of delivered vehicles + enriched finance details in a single API call
// (replaces the previous N+1 /finance/{id} loop).
async function fetchDeliveredFull() {
  const { data } = await api.get("/finance", { params: { full: true } });
  return data.map(r => ({
    vehicle_id: r.id,
    id: r.id,
    model: r.model,
    chassis_number: r.chassis_number,
    engine_number: r.engine_number,
    branch: r.branch,
    ndp: r.ndp,
    customer_name: r.customer_name || "—",
    customer_phone: r.customer_phone || "—",
    date_of_delivery: r.date_of_delivery || null,
    ex_showroom_price: r.ex_showroom_price || 0,
    loan_amount: r.loan_amount || 0,
    discount: r.discount || 0,
    down_payment: r.down_payment || 0,
    total_paid: r.total_paid || 0,
    total_vehicle_price: r.total_vehicle_price || 0,
    financer_name: r.financer_name || "—",
    remaining_balance: r.remaining_balance || 0,
    payment_type: r.payment_type || "Loan",
    days_idle: r.days_idle,
    last_payment_at: r.last_payment_at,
    finance_status: (r.remaining_balance || 0) > 0 ? "Pending" : "Paid Up",
  }));
}

const buildWhatsAppLink = (row) => {
  const phone = String(row.customer_phone || "").replace(/\D/g, "");
  if (!phone) return null;
  const cc = phone.length === 10 ? "91" : "";
  const balance = new Intl.NumberFormat("en-IN").format(Math.round(row.remaining_balance || 0));
  const msg = `Namaste ${row.customer_name || "Customer"},\n\nA friendly reminder from India Atul Motors regarding your outstanding balance of ₹${balance} for your ${row.model} (Chassis ${row.chassis_number}).\n\nKindly contact us on 9031073394 at your earliest convenience to settle the amount.\n\nThank you.`;
  return `https://wa.me/${cc}${phone}?text=${encodeURIComponent(msg)}`;
};

export default function Reports() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const canSeeNDP = user?.role === "admin" || user?.role === "finance";
  const canEditDOD = user?.role === "admin";
  const [tab, setTab] = useState(params.get("tab") || "stock");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(params.get("status") || "");
  const [financeFilter, setFinanceFilter] = useState(params.get("filter") || "");
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [title, setTitle] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [dodEdit, setDodEdit] = useState(null); // {vehicle_id, chassis_number, model, date_of_delivery}
  const [loading, setLoading] = useState(false);
  const [fySummary, setFySummary] = useState(null); // {summary, by_branch, by_model}
  const [disbData, setDisbData] = useState(null); // {rows, summary, matrix}
  const [disbBranch, setDisbBranch] = useState("");
  const [disbFinancer, setDisbFinancer] = useState("");
  const [disbStatus, setDisbStatus] = useState("");
  const [fyMode, setFyMode] = useState("fy"); // 'fy' | 'range'
  const currentFy = () => {
    const d = new Date();
    const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
    return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
  };
  const [fyValue, setFyValue] = useState(currentFy());
  // Per-tab client cache keyed by tab + date range — kills the "reloading" flicker
  // when users click between tabs. Cleared via the Refresh button.
  const cacheRef = useRef({});
  const cacheKey = () => `${tab}:${dateFrom || ""}:${dateTo || ""}:${financeFilter || ""}`;

  // Sync URL when tab / statusFilter / financeFilter changes
  useEffect(() => {
    const next = new URLSearchParams();
    next.set("tab", tab);
    if (statusFilter) next.set("status", statusFilter);
    if (financeFilter) next.set("filter", financeFilter);
    setParams(next, { replace: true });
  }, [tab, statusFilter, financeFilter, setParams]);

  useEffect(() => { run(); /* eslint-disable-next-line */ }, [tab, dateFrom, dateTo, financeFilter, fyMode, fyValue, disbBranch, disbFinancer, disbStatus]);
  useEffect(() => {
    setSearch("");
    // On Dues Files auto-load (financeFilter=pending), default sort = remaining balance DESC.
    if (tab === "finance" && financeFilter === "pending") {
      setSortKey("remaining_balance");
      setSortDir("desc");
    } else {
      setSortKey(null);
      setSortDir("asc");
    }
  }, [tab, financeFilter]);

  const run = async (force = false) => {
    const key = cacheKey();
    if (!force && cacheRef.current[key]) {
      const c = cacheRef.current[key];
      setRows(c.rows); setColumns(c.columns); setTitle(c.title);
      return;
    }
    setLoading(true);
    try {
      await _run();
    } finally {
      setLoading(false);
    }
  };

  const persistCache = (rowsArg, columnsArg, titleArg) => {
    cacheRef.current[cacheKey()] = { rows: rowsArg, columns: columnsArg, title: titleArg };
    setRows(rowsArg); setColumns(columnsArg); setTitle(titleArg);
  };

  const _run = async () => {
    if (tab === "stock") {
      const { data } = await api.get("/vehicles");
      persistCache(data, [
        SR_COL,
        { key: "model", label: "Model" },
        { key: "chassis_number", label: "Chassis" },
        { key: "engine_number", label: "Engine" },
        { key: "color", label: "Color" },
        { key: "manufacturing_year", label: "Year" },
        { key: "branch", label: "Branch" },
        { key: "status", label: "Status" },
        ...(canSeeNDP ? [{ key: "ndp", label: "NDP", fmt: (v) => INR(v || 0) }] : []),
      ], "Vehicle Stock Report");
    } else if (tab === "transfers") {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/transfers", { params });
      persistCache(data, [
        SR_COL,
        { key: "created_at", label: "Date", fmt: (v) => new Date(v).toLocaleString("en-IN") },
        { key: "model", label: "Model" },
        { key: "chassis_number", label: "Chassis" },
        { key: "from_branch", label: "From" },
        { key: "to_branch", label: "To" },
        { key: "transferred_by", label: "By" },
        { key: "status", label: "Status" },
        { key: "received_by", label: "Received By" },
        { key: "date_of_delivery", label: "Delivered On",
          fmt: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—" },
        { key: "financer_name", label: "Financer", fmt: (v) => v || "—" },
      ], "Vehicle Transfer Report");
    } else if (tab === "finance" || tab === "delivery") {
      const enriched = await fetchDeliveredFull();
      let filtered = enriched;
      if (dateFrom || dateTo) {
        filtered = enriched.filter(r => {
          if (!r.date_of_delivery) return false;
          if (dateFrom && r.date_of_delivery < dateFrom) return false;
          if (dateTo && r.date_of_delivery > dateTo) return false;
          return true;
        });
      }
      if (tab === "finance" && financeFilter === "pending") {
        filtered = filtered.filter(r => (r.remaining_balance || 0) > 0);
      }
      const renderDOD = (v, r) => {
        const disp = v ? new Date(v).toLocaleDateString("en-GB") : "—";
        if (!canEditDOD) return disp;
        return (
          <span className="inline-flex items-center gap-1.5">
            <span>{disp}</span>
            <button
              data-testid={`edit-dod-${r?.vehicle_id || r?.chassis_number}`}
              onClick={() => setDodEdit({
                vehicle_id: r.vehicle_id,
                chassis_number: r.chassis_number,
                model: r.model,
                date_of_delivery: v || "",
              })}
              className="p-1 rounded hover:bg-blue-50 text-blue-600"
              title="Edit Date of Delivery">
              <Pencil className="w-3 h-3" />
            </button>
          </span>
        );
      };
      persistCache(filtered, [
        SR_COL,
        { key: "customer_name", label: "Customer Name" },
        { key: "customer_phone", label: "Mobile" },
        { key: "date_of_delivery", label: "Date of Delivery",
          fmt: renderDOD,
          exportFmt: (v) => v ? new Date(v).toLocaleDateString("en-GB") : "—" },
        { key: "model", label: "Model" },
        { key: "chassis_number", label: "Chassis" },
        { key: "branch", label: "Branch" },
        { key: "total_vehicle_price", label: "Vehicle Price", fmt: (v) => INR(v) },
        { key: "total_paid", label: "Total Received", fmt: (v) => INR(v) },
        { key: "remaining_balance", label: "Remaining Balance",
          thClass: "bg-amber-50 text-amber-800",
          fmt: (v) => (
            <span className={`font-semibold ${v > 0 ? "text-amber-700" : "text-emerald-700"}`}>
              {INR(v)}
            </span>
          ),
          exportFmt: (v) => v },
        ...(financeFilter === "pending" ? [
          { key: "days_idle", label: "Idle Days",
            fmt: (v) => v == null ? "—" : (
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${v > 30 ? "bg-red-100 text-red-800" : v > 14 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>
                {v}d
              </span>
            ),
            exportFmt: (v) => v == null ? "" : v },
        ] : []),
        { key: "payment_type", label: "Payment Type" },
        { key: "financer_name", label: "Financer" },
        { key: "finance_status", label: "Status",
          fmt: (v) => (
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${v === "Pending" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
              {v}
            </span>
          ),
          exportFmt: (v) => v },
        ...(financeFilter === "pending" ? [
          { key: "_remind", label: "Remind",
            fmt: (_, r) => {
              const url = buildWhatsAppLink(r);
              if (!url) return <span className="text-slate-300 text-xs">no phone</span>;
              return (
                <button
                  data-testid={`whatsapp-${r.vehicle_id}`}
                  onClick={async (e) => {
                    e.stopPropagation();
                    try { await api.post(`/finance/${r.vehicle_id}/whatsapp-reminder`); } catch (_) {}
                    window.open(url, "_blank");
                  }}
                  title="Send WhatsApp payment reminder"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium">
                  <MessageCircle className="w-3 h-3" /> Remind
                </button>
              );
            },
            exportFmt: () => "" },
        ] : []),
        { key: "ex_showroom_price", label: "Ex-Showroom", fmt: (v) => INR(v) },
        { key: "loan_amount", label: "Loan", fmt: (v) => INR(v) },
        { key: "discount", label: "Discount", fmt: (v) => INR(v) },
        { key: "down_payment", label: "Down Payment", fmt: (v) => INR(v) },
        ...(canSeeNDP ? [{ key: "ndp", label: "NDP", fmt: (v) => INR(v || 0) }] : []),
      ], tab === "delivery" ? "Date of Delivery Report" : (financeFilter === "pending" ? "Dues Files" : "Finance Report"));
    } else if (tab === "model" || tab === "branch") {
      const [enriched, allRes] = await Promise.all([
        fetchDeliveredFull(),
        api.get("/vehicles"),
      ]);
      const all = allRes.data;
      const groupKey = tab === "model" ? "model" : "branch";
      const grouped = {};
      for (const v of all) {
        const g = v[groupKey] || "Unknown";
        if (!grouped[g]) grouped[g] = { key: g, total: 0, in_stock: 0, delivered: 0, delivered_value: 0 };
        grouped[g].total++;
        if (v.status === "In Stock") grouped[g].in_stock++;
        if (v.status === "Delivered") grouped[g].delivered++;
      }
      for (const e of enriched) {
        const g = e[groupKey] || "Unknown";
        if (grouped[g]) grouped[g].delivered_value += (e.ex_showroom_price || 0);
      }
      persistCache(Object.values(grouped), [
        SR_COL,
        { key: "key", label: tab === "model" ? "Model" : "Branch" },
        { key: "total", label: "Total Vehicles" },
        { key: "in_stock", label: "In Stock" },
        { key: "delivered", label: "Delivered" },
        { key: "delivered_value", label: "Delivered Value", fmt: (v) => INR(v) },
      ], tab === "model" ? "Model-Wise Report" : "Branch-Wise Report");
    } else if (tab === "profit") {
      const params = {};
      const fy = new URLSearchParams(window.location.search).get("fy");
      if (fy) params.fy = fy;
      else {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      const { data } = await api.get("/dashboard/net-profit", { params });
      persistCache(data.entries || [], [
        SR_COL,
        { key: "customer_name", label: "Customer Name" },
        { key: "customer_phone", label: "Mobile" },
        { key: "model", label: "Model" },
        { key: "chassis_number", label: "Chassis" },
        { key: "branch", label: "Branch" },
        { key: "date_of_delivery", label: "Date of Delivery",
          fmt: (v) => v ? new Date(v).toLocaleDateString("en-GB") : "—",
          exportFmt: (v) => v ? new Date(v).toLocaleDateString("en-GB") : "—" },
        { key: "ex_showroom_price", label: "Ex-Showroom", fmt: (v) => INR(v) },
        { key: "discount", label: "Discount", fmt: (v) => INR(v) },
        { key: "ndp", label: "NDP", fmt: (v) => INR(v) },
        { key: "net_profit", label: "Net Profit",
          thClass: "bg-emerald-50 text-emerald-800",
          fmt: (v) => (
            <span className={`font-semibold ${v > 0 ? "text-emerald-700" : "text-red-600"}`}>
              {INR(v)}
            </span>
          ),
          exportFmt: (v) => v },
      ], `Net Profit Report — ${INR(data.total_net_profit)} across ${data.delivered_count} vehicles`);
    } else if (tab === "disbursement") {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (disbBranch) params.branch = disbBranch;
      if (disbFinancer) params.financer = disbFinancer;
      if (disbStatus) params.status = disbStatus;
      const { data } = await api.get("/reports/disbursement", { params });
      setDisbData(data);
      persistCache(data.rows || [], [
        SR_COL,
        { key: "customer_name", label: "Customer Name" },
        { key: "customer_phone", label: "Mobile" },
        { key: "model", label: "Model" },
        { key: "chassis_number", label: "Chassis" },
        { key: "branch", label: "Branch" },
        { key: "financer_name", label: "Financer" },
        { key: "loan_amount", label: "Loan Amount", fmt: (v) => INR(v || 0), exportFmt: (v) => v },
        { key: "date_of_delivery", label: "Delivery Date",
          fmt: (v) => v ? String(v).slice(0, 10) : "—" },
        { key: "loan_disbursement", label: "Disbursement",
          fmt: (v) => (
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
              v === "Yes" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"
            }`}>{v || "No"}</span>
          ),
          exportFmt: (v) => v || "No" },
        { key: "disbursement_date", label: "Disbursement Date",
          fmt: (v) => v ? String(v).slice(0, 10) : "—" },
        { key: "delivery_order", label: "Delivery Order",
          fmt: (v) => (
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
              v === "Yes" ? "bg-teal-100 text-teal-800" : "bg-indigo-100 text-indigo-800"
            }`}>{v || "No"}</span>
          ),
          exportFmt: (v) => v || "No" },
      ], `Disbursement Report — ₹${(data.summary?.total_disbursed_amount || 0).toLocaleString("en-IN")} across ${data.summary?.disbursed_count || 0} disbursed / ${data.summary?.pending_count || 0} pending`);
    } else if (tab === "fy-summary") {
      const params = fyMode === "fy" ? { fy: fyValue } : {
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
      };
      const { data } = await api.get("/reports/fy-summary", { params });
      setFySummary(data);
      persistCache([], [], fyMode === "fy"
        ? `Financial Year Summary — FY ${fyValue}`
        : `Consolidated Summary (${dateFrom || "…"} → ${dateTo || "…"})`);
    }
  };

  const refresh = () => {
    cacheRef.current = {};
    run(true);
  };

  // Client-side search + sort — preserves filters/dates on export.
  // We assign SR. No. AFTER filtering + sorting so it always reflects the current view.
  const displayedRows = useMemo(() => {
    let out = [...rows];
    if (search) {
      const q = search.toLowerCase();
      out = out.filter(r => Object.values(r).some(v => String(v ?? "").toLowerCase().includes(q)));
    }
    if (sortKey && sortKey !== "_sr") {
      out.sort((a, b) => {
        const av = a[sortKey], bv = b[sortKey];
        if (av == null) return 1; if (bv == null) return -1;
        if (typeof av === "number" && typeof bv === "number") return sortDir === "asc" ? av - bv : bv - av;
        return sortDir === "asc"
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      });
    }
    return out.map((r, i) => ({ ...r, _sr: i + 1 }));
  }, [rows, search, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const showDateFilter = ["transfers", "delivery", "disbursement"].includes(tab);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="reports-heading">Reports</h1>
          <p className="text-sm text-slate-500 mt-1">Search, filter and export dealership data to Excel or PDF</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="report-refresh" onClick={refresh}
            title="Refresh data (bypass cache)"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-300 hover:bg-slate-50 text-sm text-slate-600">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button data-testid="report-export-excel" onClick={() => exportToExcel(title, displayedRows, columns)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button data-testid="report-export-pdf" onClick={() => exportToPDF(title, displayedRows, columns)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-blue bg-brand-blue-hover text-white text-sm">
            <FileText className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.filter(t => {
          if (t.adminOnly) return user?.role === "admin";
          if (t.financeOrAdmin) return user?.role === "admin" || user?.role === "finance";
          return true;
        }).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`report-tab-${t.key}`}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === t.key ? "bg-brand-blue text-white" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
            }`}>{t.label}</button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 flex flex-wrap gap-3 items-end">
        <label className="text-sm flex-1 min-w-[200px]">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Search</div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by any field…"
              className="w-full pl-8 pr-3 py-2 rounded-md border border-slate-300 text-sm" data-testid="report-search" />
          </div>
        </label>
        {showDateFilter && (
          <>
            <label className="text-sm">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">From</div>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="px-3 py-2 rounded-md border border-slate-300 text-sm" data-testid="report-date-from" />
            </label>
            <label className="text-sm">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">To</div>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="px-3 py-2 rounded-md border border-slate-300 text-sm" data-testid="report-date-to" />
            </label>
          </>
        )}
        {tab === "disbursement" && (
          <>
            <label className="text-sm">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Status</div>
              <select value={disbStatus} onChange={(e) => setDisbStatus(e.target.value)}
                data-testid="disbursement-report-status"
                className="px-3 py-2 rounded-md border border-slate-300 text-sm bg-white">
                <option value="">All</option>
                <option value="Yes">Disbursed</option>
                <option value="No">Pending</option>
              </select>
            </label>
            <label className="text-sm">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Financer</div>
              <input value={disbFinancer} onChange={(e) => setDisbFinancer(e.target.value)}
                placeholder="e.g. KAFL"
                data-testid="disbursement-report-financer"
                className="px-3 py-2 rounded-md border border-slate-300 text-sm w-36" />
            </label>
            <label className="text-sm">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Branch</div>
              <input value={disbBranch} onChange={(e) => setDisbBranch(e.target.value)}
                placeholder="e.g. Purnea HO"
                data-testid="disbursement-report-branch"
                className="px-3 py-2 rounded-md border border-slate-300 text-sm w-36" />
            </label>
          </>
        )}
        <div className="text-xs text-slate-500 ml-auto">
          Showing <b>{displayedRows.length}</b> of {rows.length} rows
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg table-scroll">
        {tab === "fy-summary" ? (
          <FySummaryView data={fySummary} fyMode={fyMode} setFyMode={setFyMode}
            fyValue={fyValue} setFyValue={setFyValue}
            dateFrom={dateFrom} dateTo={dateTo}
            setDateFrom={setDateFrom} setDateTo={setDateTo} loading={loading} />
        ) : (
          <table className="data-table w-full" data-testid="report-table">
          <thead>
            <tr>
              {columns.map(c => (
                <th key={c.key}
                  onClick={() => toggleSort(c.key)}
                  className={`cursor-pointer select-none hover:bg-slate-100 ${c.thClass || ""}`}>
                  {c.label}
                  {sortKey === c.key && (
                    <span className="ml-1 text-slate-400">{sortDir === "asc" ? "▲" : "▼"}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayedRows.length === 0 && (
              <tr><td colSpan={columns.length} className="text-center py-10 text-slate-400 text-sm">
                {loading ? "Loading…" : "No data"}
              </td></tr>
            )}
            {displayedRows.map((r, i) => (
              <tr key={i}>
                {columns.map(c => {
                  const monoCell = c.key === "chassis_number" || c.key === "engine_number";
                  const cls = [c.tdClass || "", monoCell ? "font-mono text-xs" : ""].filter(Boolean).join(" ");
                  return (
                    <td key={c.key} className={cls}>
                      {c.fmt ? c.fmt(r[c.key], r) : (r[c.key] ?? "—")}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>

      {tab === "disbursement" && disbData && (
        <DisbursementMatrix data={disbData} />
      )}

      {dodEdit && (
        <EditDODModal
          entry={dodEdit}
          onClose={() => setDodEdit(null)}
          onSaved={() => { setDodEdit(null); cacheRef.current = {}; run(true); }}
        />
      )}
    </div>
  );
}

function EditDODModal({ entry, onClose, onSaved }) {
  const [date, setDate] = useState(entry.date_of_delivery ? String(entry.date_of_delivery).slice(0, 10) : "");
  const [saving, setSaving] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const save = async () => {
    if (!date) { toast.error("Date of Delivery cannot be blank."); return; }
    if (date > today) { toast.error("Date of Delivery cannot be a future date."); return; }
    setSaving(true);
    try {
      await api.patch(`/finance/${entry.vehicle_id}/date-of-delivery`, {
        date_of_delivery: date,
      });
      toast.success("Date of Delivery updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const displayOld = entry.date_of_delivery
    ? new Date(entry.date_of_delivery).toLocaleDateString("en-GB")
    : "—";

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
         data-testid="edit-dod-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Edit</div>
            <h2 className="font-heading text-xl font-semibold text-slate-900">Date of Delivery</h2>
            <div className="text-xs text-slate-500 mt-1 font-mono">{entry.model} · {entry.chassis_number}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
                  data-testid="edit-dod-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-4">
          <div className="text-xs text-slate-500 mb-1">Current: <span className="font-medium text-slate-700">{displayOld}</span></div>
          <label className="block">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">
              New Date (DD/MM/YYYY)
            </div>
            <input
              type="date"
              value={date}
              max={today}
              onChange={(e) => setDate(e.target.value)}
              data-testid="edit-dod-input"
              className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-800"
            />
          </label>
          <div className="text-[11px] text-slate-400 mt-2">
            Future dates are not allowed. Change syncs immediately across Finance, Invoice, Reports and Dashboard.
          </div>
        </div>
        <div className="px-6 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm border border-slate-300 hover:bg-slate-50">
            Cancel
          </button>
          <button onClick={save} disabled={saving}
                  data-testid="edit-dod-save"
                  className="px-4 py-2 rounded-md text-sm bg-brand-blue bg-brand-blue-hover text-white disabled:opacity-60">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FySummaryView({ data, fyMode, setFyMode, fyValue, setFyValue,
                        dateFrom, dateTo, setDateFrom, setDateTo, loading }) {
  const today = new Date();
  const y0 = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  const fyOpts = [];
  for (let i = -3; i <= 1; i++) {
    const y = y0 + i;
    fyOpts.push(`${y}-${String((y + 1) % 100).padStart(2, "0")}`);
  }
  const s = data?.summary || {};
  const branchCols = [
    { key: "_sr", label: "SR. No." },
    { key: "key", label: "Branch" },
    { key: "purchased", label: "Purchased" },
    { key: "delivered", label: "Delivered" },
    { key: "sales_value", label: "Sales Value", fmt: (v) => INR(v) },
    { key: "loan", label: "Loan", fmt: (v) => INR(v) },
    { key: "paid", label: "Paid", fmt: (v) => INR(v) },
    { key: "outstanding", label: "Outstanding", fmt: (v) => INR(v) },
    { key: "profit", label: "Profit", fmt: (v) => INR(v) },
  ];
  const modelCols = branchCols.map(c => c.key === "key" ? { ...c, label: "Model" } : c);
  const branchRows = (data?.by_branch || []).map((r, i) => ({ ...r, _sr: i + 1 }));
  const modelRows  = (data?.by_model  || []).map((r, i) => ({ ...r, _sr: i + 1 }));

  return (
    <div className="p-6" data-testid="fy-summary-view">
      {/* Filter mode */}
      <div className="flex flex-wrap items-end gap-3 mb-6 pb-4 border-b border-slate-100">
        <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-xs">
          <button data-testid="fy-mode-fy" onClick={() => setFyMode("fy")}
            className={`px-3 py-1.5 ${fyMode === "fy" ? "bg-brand-blue text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
            Financial Year
          </button>
          <button data-testid="fy-mode-range" onClick={() => setFyMode("range")}
            className={`px-3 py-1.5 ${fyMode === "range" ? "bg-brand-blue text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
            Month Range
          </button>
        </div>
        {fyMode === "fy" ? (
          <select value={fyValue} onChange={(e) => setFyValue(e.target.value)}
            data-testid="fy-select"
            className="px-3 py-1.5 border border-slate-300 rounded-md text-sm bg-white">
            {fyOpts.map(v => <option key={v} value={v}>FY {v}</option>)}
          </select>
        ) : (
          <>
            <label className="text-xs">
              <div className="uppercase tracking-wider text-slate-500 mb-1">From</div>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                data-testid="fy-range-from"
                className="px-3 py-1.5 border border-slate-300 rounded-md text-sm" />
            </label>
            <label className="text-xs">
              <div className="uppercase tracking-wider text-slate-500 mb-1">To</div>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                data-testid="fy-range-to"
                className="px-3 py-1.5 border border-slate-300 rounded-md text-sm" />
            </label>
          </>
        )}
        <div className="ml-auto flex gap-2">
          <button data-testid="fy-export-excel"
            onClick={() => exportToExcel(`FY_Summary_${fyMode === "fy" ? fyValue : "range"}_branch`, branchRows, branchCols)}
            className="text-xs px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white">
            Branch Excel
          </button>
          <button data-testid="fy-export-model-excel"
            onClick={() => exportToExcel(`FY_Summary_${fyMode === "fy" ? fyValue : "range"}_model`, modelRows, modelCols)}
            className="text-xs px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white">
            Model Excel
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <SumTile label="Purchased" value={s.purchased} testid="fy-sum-purchased" />
        <SumTile label="Delivered" value={s.delivered} tone="emerald" testid="fy-sum-delivered" />
        <SumTile label="In Stock" value={s.in_stock} tone="blue" testid="fy-sum-in-stock" />
        <SumTile label="Sales Value" value={INR(s.sales_value || 0)} tone="slate" testid="fy-sum-sales" />
        <SumTile label="Loan" value={INR(s.loan || 0)} testid="fy-sum-loan" />
        <SumTile label="Payment Received" value={INR(s.paid || 0)} tone="emerald" testid="fy-sum-paid" />
        <SumTile label="Outstanding" value={INR(s.outstanding || 0)} tone="amber" testid="fy-sum-outstanding" />
        <SumTile label="Profit" value={INR(s.profit || 0)} tone="emerald" testid="fy-sum-profit" />
        <SumTile label="Incentive" value={INR(s.incentive || 0)} tone="blue" testid="fy-sum-incentive" />
      </div>

      {loading && <div className="text-center py-6 text-sm text-slate-400">Loading…</div>}

      {/* Branch table */}
      <div className="mb-8">
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Branch-wise breakdown</div>
        <div className="border border-slate-200 rounded-lg overflow-x-auto">
          <table className="data-table w-full" data-testid="fy-branch-table">
            <thead><tr>{branchCols.map(c => <th key={c.key} className={c.thClass || ""}>{c.label}</th>)}</tr></thead>
            <tbody>
              {branchRows.length === 0 && (
                <tr><td colSpan={branchCols.length} className="text-center py-6 text-slate-400 text-sm">No branch data</td></tr>
              )}
              {branchRows.map((r, i) => (
                <tr key={i}>{branchCols.map(c => (
                  <td key={c.key} className={c.tdClass || ""}>
                    {c.fmt ? c.fmt(r[c.key], r) : (r[c.key] ?? "—")}
                  </td>
                ))}</tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Model table */}
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Model-wise breakdown</div>
        <div className="border border-slate-200 rounded-lg overflow-x-auto">
          <table className="data-table w-full" data-testid="fy-model-table">
            <thead><tr>{modelCols.map(c => <th key={c.key} className={c.thClass || ""}>{c.label}</th>)}</tr></thead>
            <tbody>
              {modelRows.length === 0 && (
                <tr><td colSpan={modelCols.length} className="text-center py-6 text-slate-400 text-sm">No model data</td></tr>
              )}
              {modelRows.map((r, i) => (
                <tr key={i}>{modelCols.map(c => (
                  <td key={c.key} className={c.tdClass || ""}>
                    {c.fmt ? c.fmt(r[c.key], r) : (r[c.key] ?? "—")}
                  </td>
                ))}</tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const TONE_CLS = {
  slate: "bg-slate-50 border-slate-200 text-slate-800",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  blue: "bg-blue-50 border-blue-200 text-blue-800",
  amber: "bg-amber-50 border-amber-200 text-amber-800",
};

function SumTile({ label, value, tone = "slate", testid }) {
  return (
    <div className={`p-3 rounded-md border ${TONE_CLS[tone] || TONE_CLS.slate}`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
      <div className="font-heading text-xl font-bold mt-0.5">{value}</div>
    </div>
  );
}



function DisbursementMatrix({ data }) {
  const financers = data?.matrix?.financers || [];
  const months = data?.matrix?.months || [];
  const s = data?.summary || {};
  const monthLabel = (ym) => {
    if (!ym || ym.length < 7) return ym || "—";
    const [y, m] = ym.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 mt-6" data-testid="disbursement-matrix">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">Month-wise Financer Split</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Disbursed amount broken down by financer, keyed on disbursement date
          </p>
        </div>
        <div className="flex gap-4 text-xs flex-wrap">
          <div>
            <div className="uppercase tracking-wider text-slate-500">Disbursed</div>
            <div className="font-heading text-lg font-bold text-emerald-700" data-testid="disb-summary-count">
              {s.disbursed_count || 0}
            </div>
          </div>
          <div>
            <div className="uppercase tracking-wider text-slate-500">Pending</div>
            <div className="font-heading text-lg font-bold text-amber-700" data-testid="disb-summary-pending">
              {s.pending_count || 0}
            </div>
          </div>
          <div>
            <div className="uppercase tracking-wider text-slate-500">Total Amount</div>
            <div className="font-heading text-lg font-bold text-slate-900" data-testid="disb-summary-amount">
              {INR(s.total_disbursed_amount || 0)}
            </div>
          </div>
          <div>
            <div className="uppercase tracking-wider text-slate-500">DO Issued</div>
            <div className="font-heading text-lg font-bold text-teal-700" data-testid="disb-summary-do-issued">
              {s.do_issued_count || 0}
            </div>
          </div>
          <div>
            <div className="uppercase tracking-wider text-slate-500">DO Pending</div>
            <div className="font-heading text-lg font-bold text-indigo-700" data-testid="disb-summary-do-pending">
              {s.do_pending_count || 0}
            </div>
          </div>
        </div>
      </div>
      {financers.length === 0 || months.length === 0 ? (
        <div className="text-sm text-slate-400 italic text-center py-6">
          No disbursed rows to display in the matrix for the current filters.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-md overflow-x-auto">
          <table className="data-table w-full" data-testid="disbursement-matrix-table">
            <thead>
              <tr>
                <th>Month</th>
                {financers.map(fn => <th key={fn} className="text-right">{fn}</th>)}
                <th className="text-right bg-emerald-50 text-emerald-800">Row Total</th>
              </tr>
            </thead>
            <tbody>
              {months.map(row => (
                <tr key={row.month}>
                  <td className="font-medium">{monthLabel(row.month)}</td>
                  {financers.map(fn => (
                    <td key={fn} className="text-right text-sm font-mono">
                      {row.cells[fn]?.amount ? INR(row.cells[fn].amount) : "—"}
                      {row.cells[fn]?.count ? (
                        <span className="ml-1 text-[10px] text-slate-400">×{row.cells[fn].count}</span>
                      ) : null}
                    </td>
                  ))}
                  <td className="text-right font-semibold bg-emerald-50 text-emerald-800">{INR(row.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
